import { formatValue, getExtractionStatus } from '../components/DataPointsTable';
import {
  accountNote, asOfOf, fieldRowsOf, institutionOf, matchKeyOf, rowAnnotations, titleOf,
} from './clientReport';
import { formatWhen } from './dateUtils';
import { personName } from './people';
import { fieldLabel } from './reportSearch';

/**
 * The eMoney report as the export model the server lays out as a PDF or
 * Word file (backend report_export.py): the same accounts, rows, corrected
 * values, notes and eMoney marks the page shows, as formatted text.
 */

// Server-side limits (models.ReportExportModel); longer text is cut, not refused.
const LIMITS = { value: 4000, label: 500, title: 500, source: 1000, name: 300 };

const clip = (text, max) => {
  const value = text === null || text === undefined ? '' : String(text);
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isEmpty = (value) => value === null || value === undefined || value === '';
const GAP_VALUES = ['unknown', 'unclear'];

const WITHOUT_DATA_LABELS = {
  not_applicable: 'Not applicable',
  failed: 'Extraction failed',
  none: 'Summary only',
};

/** A value as the table cell shows it (lists of objects are handled as tables). */
export const exportText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.filter((v) => v !== null && v !== undefined)
      .map((v) => (isPlainObject(v) || Array.isArray(v) ? JSON.stringify(v) : formatValue(v)))
      .join(', ');
  }
  if (isPlainObject(value)) {
    return Object.entries(value)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${fieldLabel(k)}: ${Array.isArray(v) || isPlainObject(v) ? JSON.stringify(v) : formatValue(v)}`)
      .join(' · ');
  }
  return formatValue(value);
};

const tableRow = (label, value, nested) => {
  const columns = [];
  value.forEach((row) => Object.keys(row).forEach((key) => { if (!columns.includes(key)) columns.push(key); }));
  return {
    kind: 'table',
    label: clip(label, LIMITS.label),
    nested,
    columns: columns.map((c) => clip(fieldLabel(c), LIMITS.label)),
    rows: value.map((row) => columns.map((c) => clip(exportText(row[c]), LIMITS.value))),
  };
};

const accountModel = (account, viewer) => {
  const { doc, fields, ref } = account.current;
  const annotations = doc.annotations;
  const institution = institutionOf(fields);
  const title = titleOf(fields);
  const matchKey = matchKeyOf(fields);
  const asOf = asOfOf(fields);
  const subtitle = [
    title && institution ? institution : null,
    matchKey && matchKey.length <= 6 ? `Account ••${matchKey}` : null,
    asOf ? `As of ${asOf}` : null,
  ].filter(Boolean).join(' · ');

  const rows = fieldRowsOf(fields).map((row) => {
    const nested = row.childKey !== undefined;
    if (row.group) return { kind: 'group', label: clip(fieldLabel(row.key), LIMITS.label) };
    const label = fieldLabel(nested ? row.childKey : row.key);
    const value = nested ? fields[row.key][row.childKey] : fields[row.key];
    if (Array.isArray(value) && value.length && value.every(isPlainObject)) return tableRow(label, value, nested);
    const { correction, mark } = rowAnnotations(annotations, ref, row);
    return {
      kind: 'value',
      label: clip(label, LIMITS.label),
      nested,
      value: clip(exportText(value), LIMITS.value),
      gap: typeof value === 'string' && GAP_VALUES.includes(value.trim().toLowerCase()),
      edited: correction ? {
        original: clip(isEmpty(correction.original) ? '(empty)' : exportText(correction.original), LIMITS.value),
        by: clip(personName(correction.by, viewer), LIMITS.name),
        at: formatWhen(correction.at),
      } : null,
      sync: mark ? (mark.stale ? 'changed' : 'entered') : null,
    };
  });

  const note = accountNote(annotations, ref);
  return {
    title: clip(title || institution || doc.subTypeName || doc.docTypeName || 'Account', LIMITS.title),
    subtitle: clip(subtitle, LIMITS.title),
    rows,
    note: note ? {
      text: clip(note.text, LIMITS.value),
      by: clip(personName(note.by, viewer), LIMITS.name),
      at: formatWhen(note.at),
    } : null,
    source: clip(doc.documentName || doc.fileId, LIMITS.source),
  };
};

const withoutDataModel = (doc) => {
  const extraction = getExtractionStatus(doc.dataPoints, doc.dataPointsDetails);
  return {
    name: clip(doc.documentName || doc.fileId, LIMITS.source),
    status: WITHOUT_DATA_LABELS[extraction.kind] || WITHOUT_DATA_LABELS.none,
    reason: clip(extraction.reason || '', LIMITS.value),
  };
};

/**
 * report: buildClientReport output (the full report, not a search's view).
 * viewer: { name, ids } for showing names instead of account ids.
 */
export const buildExportModel = (report, { clientName, viewer, generatedAt = new Date() }) => ({
  clientName: clip(clientName, 255),
  generatedAt: generatedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
  sets: (report || []).map((set) => ({
    label: clip(set.label, LIMITS.name),
    sections: set.sections.map((section) => ({
      label: clip(section.label, LIMITS.name),
      accounts: section.accounts.map((account) => accountModel(account, viewer)),
      withoutData: section.withoutData.map(withoutDataModel),
    })),
  })),
});

/** "Client A - eMoney report - 2026-10-06.pdf" (the server's name for the same file). */
export const exportFilename = (clientName, format, date = new Date()) => {
  const safe = String(clientName || '').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'Client';
  const day = [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((n) => String(n).padStart(2, '0')).join('-');
  return `${safe} - eMoney report - ${day}.${format}`;
};
